import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { Zap, X, Clock, Webhook, ListChecks, AlertCircle, Copy, Check } from 'lucide-react';

const MIN_INTERVAL_SECONDS = 60;

const AutomateFlowPanel = ({ isOpen, onClose, flowId }) => {
  const [schedule, setSchedule] = useState(null);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [webhookUrl, setWebhookUrl] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const flowSaved = flowId && flowId !== 'current-flow';

  const load = useCallback(async () => {
    if (!flowSaved) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [scheduleRes, runsRes] = await Promise.all([
        apiService.getSchedule(flowId),
        apiService.getFlowRuns(flowId),
      ]);
      setSchedule(scheduleRes?.schedule || null);
      if (scheduleRes?.schedule) {
        setIntervalMinutes(Math.max(1, Math.round(scheduleRes.schedule.intervalSeconds / 60)));
      }
      setRuns(runsRes?.runs || []);
    } catch (err) {
      setError(err.message || 'Failed to load automation settings.');
    } finally {
      setLoading(false);
    }
  }, [flowId, flowSaved]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  if (!isOpen) return null;

  const saveSchedule = async (enabled) => {
    setSaving(true);
    setError('');
    try {
      const intervalSeconds = Math.max(MIN_INTERVAL_SECONDS, Math.round(intervalMinutes * 60));
      const result = await apiService.setSchedule(flowId, { intervalSeconds, enabled });
      setSchedule(result?.schedule || null);
    } catch (err) {
      setError(err.message || 'Failed to update schedule.');
    } finally {
      setSaving(false);
    }
  };

  const generateWebhook = async () => {
    setGenerating(true);
    setError('');
    try {
      const result = await apiService.generateWebhook(flowId);
      setWebhookUrl(result?.webhookUrl || null);
    } catch (err) {
      setError(err.message || 'Failed to generate webhook URL.');
    } finally {
      setGenerating(false);
    }
  };

  const copyWebhook = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Zap size={20} className="text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Automate</h2>
              <p className="text-sm text-gray-600">
                Run this flow on a schedule, via webhook, or check recent runs
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {!flowSaved ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Save this flow first before setting up automation.
            </div>
          ) : loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : (
            <>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}

              {/* Schedule */}
              <section>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock size={14} className="inline mr-2" />
                  Schedule
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-600">minutes</span>
                  <button
                    onClick={() => saveSchedule(true)}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : schedule?.enabled ? 'Update' : 'Enable'}
                  </button>
                  {schedule?.enabled && (
                    <button
                      onClick={() => saveSchedule(false)}
                      disabled={saving}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:border-gray-400 transition-colors disabled:opacity-50"
                    >
                      Disable
                    </button>
                  )}
                </div>
                {schedule?.enabled ? (
                  <p className="text-xs text-gray-500 mt-2">
                    Runs every {Math.round(schedule.intervalSeconds / 60)} minute(s). Next run:{' '}
                    {schedule.nextRunAt ? new Date(schedule.nextRunAt * 1000).toLocaleString() : 'pending'}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">No active schedule.</p>
                )}
              </section>

              {/* Webhook */}
              <section>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Webhook size={14} className="inline mr-2" />
                  Webhook
                </label>
                <button
                  onClick={generateWebhook}
                  disabled={generating}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {generating ? 'Generating…' : webhookUrl ? 'Regenerate webhook URL' : 'Generate webhook URL'}
                </button>
                {webhookUrl && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      readOnly
                      value={webhookUrl}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 p-2 border border-gray-300 rounded-md text-xs font-mono bg-gray-50"
                    />
                    <button
                      onClick={copyWebhook}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs ${
                        copied ? 'bg-green-100 text-green-700' : 'bg-gray-800 text-white hover:bg-gray-900'
                      }`}
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  POST to this URL (with an <code>idempotency-key</code> header for safe retries) to trigger this
                  flow from any external system.
                </p>
              </section>

              {/* Recent runs */}
              <section>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ListChecks size={14} className="inline mr-2" />
                  Recent runs
                </label>
                {runs.length === 0 ? (
                  <p className="text-xs text-gray-500">No runs yet.</p>
                ) : (
                  <ul className="text-xs divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
                    {runs.map((r) => (
                      <li key={r.id} className="flex items-center justify-between px-3 py-2 bg-white">
                        <span className="text-gray-700 font-medium">{r.trigger_type}</span>
                        <span
                          className={
                            r.status === 'failed'
                              ? 'text-red-600'
                              : r.status === 'completed'
                              ? 'text-green-600'
                              : 'text-gray-600'
                          }
                        >
                          {r.status}
                        </span>
                        <span className="text-gray-400">
                          {r.updated_at ? new Date(r.updated_at * 1000).toLocaleString() : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutomateFlowPanel;
